import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ReactSVG from "react-svg";
import Input from "components/Input/Input";
import Modal from "components/Modal/Modal";
import Field from "components/Field/Field";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import Footer from "views/GeneralRegister/modalComponents/Footer";
import { Select } from "views/GeneralRegister/CustomSelect";
import EyeOpened from "assets/img/eye-opened.svg";
import EyeClosed from "assets/img/eye-closed.svg";
import { useQuery } from "@apollo/react-hooks";
import { httpClient } from "settings/apolloClient.jsx";
import { screenCatalog, isScreenVisibleByDefault } from "screens.js";
import { userRoleLabels, roleOptionsFor } from "./roleTypes";
import { CURRENT_USER_ROLE_QUERY } from "./userManagementQueries";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 25px;
`;

const PasswordWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const EyeButton = styled.div`
  padding-left: 8px;
  cursor: pointer;
`;

const ScreensGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
  margin-top: 8px;
`;

const ScreenItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-weight: normal;
  cursor: pointer;
`;

const Hint = styled.small`
  color: #999;
`;

const buildInitialState = user => ({
  name: (user && user.name) || "",
  email: (user && user.email) || "",
  role: user && user.role
    ? { value: user.role, label: userRoleLabels[user.role] || user.role }
    : null,
  password: "",
  // Deviations from the role default, keyed by screen. Absent key = inherit.
  screenOverrides: (user && user.screenOverrides) || {},
});

const UserModal = ({
  user,
  isOpen,
  toggleModal,
  onSave,
  title,
  snackbarContext,
}) => {
  const isEdit = Boolean(user && user.id);
  const [form, setForm] = useState(buildInitialState(user));
  const [showPassword, setShowPassword] = useState(false);

  // The @apollo/react-hooks client is separate from the app's ApolloProvider, so
  // pass it explicitly. Scopes the assignable roles to what the acting user may grant.
  const { data: meData } = useQuery(CURRENT_USER_ROLE_QUERY, { client: httpClient, fetchPolicy: "cache-first" });
  const actingRole = meData && meData.getCurrentUser && meData.getCurrentUser.role;
  const roleOptions = roleOptionsFor(actingRole, user && user.role);

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState(user));
      setShowPassword(false);
    }
  }, [isOpen, user]);

  const roleValue = form.role ? form.role.value : null;
  const isSuperuser = roleValue === "SUPERUSER";

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const isScreenChecked = key => (
    Object.prototype.hasOwnProperty.call(form.screenOverrides, key)
      ? form.screenOverrides[key]
      : isScreenVisibleByDefault(roleValue, key)
  );

  // Store only what deviates from the role default: toggling back to the default
  // drops the key, so a new screen later just inherits the role for everyone.
  const toggleScreen = key => setForm((prev) => {
    const currentRole = prev.role ? prev.role.value : null;
    const hasOverride = Object.prototype.hasOwnProperty.call(prev.screenOverrides, key);
    const current = hasOverride ? prev.screenOverrides[key] : isScreenVisibleByDefault(currentRole, key);
    const next = !current;

    const screenOverrides = { ...prev.screenOverrides };
    if (next === isScreenVisibleByDefault(currentRole, key)) {
      delete screenOverrides[key];
    } else {
      screenOverrides[key] = next;
    }

    return { ...prev, screenOverrides };
  });

  const onSaveEntry = () => {
    if (!form.name.trim()) {
      snackbarContext.setNotificationTimeOut(Color.danger, "O campo nome é obrigatório");
      return;
    }

    if (!form.role) {
      snackbarContext.setNotificationTimeOut(Color.danger, "O campo papel é obrigatório");
      return;
    }

    if (!isEdit && !form.password) {
      snackbarContext.setNotificationTimeOut(Color.danger, "O campo senha é obrigatório");
      return;
    }

    // Drop redundant deviations (equal to the chosen role default) before saving.
    const deviations = {};
    Object.keys(form.screenOverrides).forEach((key) => {
      if (form.screenOverrides[key] !== isScreenVisibleByDefault(roleValue, key)) {
        deviations[key] = form.screenOverrides[key];
      }
    });

    onSave({
      name: form.name.trim(),
      email: form.email.trim() || null,
      role: form.role.value,
      // On edit, a blank password keeps the current one.
      password: form.password || null,
      // Superuser ignores overrides; otherwise send the deviations (or null).
      screenOverrides: isSuperuser || Object.keys(deviations).length === 0 ? null : deviations,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggleModal={toggleModal}
      size="md"
      title={title}
      footer={<Footer onConfirm={onSaveEntry} />}
    >
      <Row>
        <Field title="Nome" width="96%">
          <Input
            name="name"
            style={{ minHeight: "40px" }}
            value={form.name}
            onChange={updateField}
          />
        </Field>
      </Row>
      <Row>
        <Field title="Email" width="96%">
          <Input
            name="email"
            style={{ minHeight: "40px" }}
            value={form.email}
            onChange={updateField}
          />
        </Field>
      </Row>
      <Row>
        <Field title="Papel" width="96%">
          <Select
            name="role"
            placeholder="Escolha..."
            value={form.role}
            onChange={updateField}
            options={roleOptions}
          />
        </Field>
      </Row>
      <Row>
        <Field title={isEdit ? "Senha (deixe em branco para manter)" : "Senha"} width="96%">
          <PasswordWrapper>
            <Input
              name="password"
              type={showPassword ? "text" : "password"}
              style={{ minHeight: "40px" }}
              value={form.password}
              onChange={updateField}
            />
            <EyeButton onClick={() => setShowPassword(prev => !prev)}>
              <ReactSVG
                svgStyle={{ width: "20px", height: "20px" }}
                src={showPassword ? EyeOpened : EyeClosed}
              />
            </EyeButton>
          </PasswordWrapper>
        </Field>
      </Row>
      {!isSuperuser && (
        <Row>
          <Field title="Telas visíveis" width="96%">
            <Hint>
              Marcadas conforme o padrão do papel. Altere só o que esse usuário deve
              ver diferente — telas novas seguem o papel automaticamente.
            </Hint>
            <ScreensGrid>
              {screenCatalog.map(screenItem => (
                <ScreenItem key={screenItem.key}>
                  <Input
                    name={screenItem.key}
                    type="checkbox"
                    checked={isScreenChecked(screenItem.key)}
                    onChange={() => toggleScreen(screenItem.key)}
                  />
                  {screenItem.label}
                  {screenItem.hidden ? " (via URL)" : ""}
                </ScreenItem>
              ))}
            </ScreensGrid>
          </Field>
        </Row>
      )}
    </Modal>
  );
};

export default withSnackbarConsumer(UserModal);
